/**
 * Chart Color Constants
 * Shared color palette for charts - matches CSS variables in index.css
 */

/**
 * Shared Recharts theme for dark mode.
 * Use these for CartesianGrid, XAxis, YAxis, and tick styling
 * to avoid hardcoded light-mode hex values in each chart component.
 */
export const CHART_THEME = {
  // Grid lines — subtle but visible on dark backgrounds (ui-ux-pro-max: gridline-subtle)
  grid: 'rgba(255, 255, 255, 0.08)',
  // Axis lines and tick marks
  axis: '#334155',
  // Axis label text
  tickText: '#94A3B8',
  tickTextBold: '#F8FAFC',
  // Tooltip axis label text (fullscreen expanded ticks)
  axisLabelPrimary: '#F8FAFC',
  axisLabelSecondary: '#94A3B8',
} as const;

/** Pre-built Recharts props to reduce duplication across chart components. */
export const GRID_PROPS = { strokeDasharray: '3 3', stroke: CHART_THEME.grid } as const;
export const AXIS_TICK = { fontSize: 12, fill: CHART_THEME.tickText } as const;
export const AXIS_TICK_SM = { fontSize: 11, fill: CHART_THEME.tickText } as const;
export const AXIS_LINE = { stroke: CHART_THEME.axis } as const;

// Income source colors
export const CHART_COLORS = {
  // Income sources
  guaranteedIncome: '#06b6d4', // cyan-500 — capitalised value of State Pension + DB pensions
  statePension: '#22c55e',
  additionalPensions: '#9ca3af',
  annuityIncome: '#0d9488', // teal-600 — guaranteed for life, distinct from drawdown blues
  supplementaryIncome: '#f97316', // orange-500
  sipp: '#3b82f6',
  isa: '#8b5cf6',
  cash: '#f59e0b',
  property: '#ec4899',
  rentalIncome: '#6366f1',
  spending: '#ef4444',
  careCosts: '#b91c1c',  // red-700

  // Tax bands
  taxBasic: '#3b82f6',
  taxHigher: '#ea580c',
  taxAdditional: '#b91c1c',

  // Portfolio
  total: '#94a3b8',
  netWorth: '#22c55e',

  // Brokerage (GIA)
  brokerage: '#14b8a6',        // Teal-500
  person2Brokerage: '#2dd4bf', // Teal-400

  // Person 2 lighter variants (for joint plan net worth breakdown)
  person2Sipp: '#60a5fa',
  person2Isa: '#a78bfa',
  person2Cash: '#fbbf24',

  // Cumulative/secondary
  cumulative: '#64748b',

  // Shock scenario
  shockTotal: '#ef4444',  // red-500

  // Monte Carlo fan chart — semi-transparent fills for dark backgrounds
  mcP10P90: 'rgba(37, 99, 235, 0.15)',   // outer band (wider range, lighter)
  mcP25P75: 'rgba(37, 99, 235, 0.30)',   // inner band (higher confidence, more opaque)
  mcP50: '#60a5fa',         // blue-400 (median line — brighter for visibility)
  mcDeterministic: '#fbbf24', // amber-400 (deterministic overlay)

  // Guardrail corridor
  guardrailFloor: '#22c55e',   // green-500
  guardrailCeiling: '#9ca3af', // gray-400

  // Annotation overlays — saturated for dark backgrounds (used at low fillOpacity)
  phaseActive: '#22c55e',      // green-500 (Phase 1 - Active)
  phaseSlowing: '#f59e0b',     // amber-500 (Phase 2 - Slowing)
  phaseLater: '#8b5cf6',       // violet-500 (Phase 3 - Later)
  phaseFourth: '#06b6d4',      // cyan-500 (Phase 4, #1538 — up to 5 phases)
  phaseFifth: '#ec4899',       // pink-500 (Phase 5, #1538)
  carePeriod: '#ef4444',       // red-500
  lifeExpectancy: '#64748b',   // slate-600

  // IHT estate composition
  ihtProperty: '#b45309',     // amber-700
  ihtSipp: '#3b82f6',         // reuse SIPP blue
  ihtIsa: '#8b5cf6',          // reuse ISA purple
  ihtBrokerage: '#14b8a6',    // reuse brokerage teal
  ihtCash: '#6b7280',         // gray-500
  ihtExposed: '#ef4444',      // red-500
  ihtThreshold: '#16a34a',    // green-600

  // Compounding visualiser
  compoundingContributed: '#94a3b8', // slate-400
  compoundingGrowth: '#4f5ec2',      // primary
  compoundingComparison: '#f97316',  // orange-500
  compoundingGap: '#ef4444',         // red-500

  // Salary Sacrifice calculator
  salarySacrificeContributed: '#94a3b8', // slate-400
  salarySacrificeGrowth: '#4f5ec2',      // primary
  salarySacrificeNet: '#22c55e',         // green-500
  salarySacrificeTax: '#ef4444',         // red-500
  salarySacrificeNI: '#f97316',          // orange-500

  // Coast FIRE calculator
  coastFireContributing: '#3b82f6',      // blue-500
  coastFireCoasting: '#22c55e',          // green-500
  coastFireTarget: '#f59e0b',            // amber-500

  // Retirement MOT
  motGreen: '#22c55e',                   // green-500
  motAmber: '#f59e0b',                   // amber-500
  motRed: '#ef4444',                     // red-500
  motProjected: '#4f5ec2',              // primary
  motTarget: '#94a3b8',                  // slate-400
  motProgress: '#3b82f6',               // blue-500

  // Employer Match Maximiser
  employerMatchCurrent: '#3b82f6',       // blue-500
  employerMatchOptimal: '#22c55e',       // green-500
  employerMatchEmployer: '#f59e0b',      // amber-500
  employerMatchGrowth: '#4f5ec2',        // primary

  // Volatility Education
  volatilityKeptInvesting: '#22c55e',    // green-500
  volatilityPanicSold: '#ef4444',        // red-500
  volatilityCrashZone: '#94a3b8',        // slate-400
  volatilityRollingRange: '#93c5fd',     // blue-300

  // Pre-State Pension Gap
  pensionGapWorking: '#3b82f6',          // blue-500
  pensionGapGapYears: '#f59e0b',         // amber-500
  pensionGapSpYears: '#22c55e',          // green-500
  pensionGapShortfall: '#ef4444',        // red-500

  // NI Top-Up calculator
  niTopUpNetPension: '#22c55e',          // green-500
  niTopUpCost: '#ef4444',                // red-500
  niTopUpBreakEven: '#f59e0b',           // amber-500

  // Alternative Plan Comparison
  alternativePlan: '#f97316',            // orange-500

  // Spending Strategy Delta Chart
  spendingDeltaPositive: '#22c55e',    // green-500
  spendingDeltaNegative: '#ef4444',    // red-500
  spendingDeltaNeutral: '#94a3b8',     // slate-400
} as const;

// Type for chart color keys
export type ChartColorKey = keyof typeof CHART_COLORS;

/**
 * Get a chart color by key with fallback
 */
export function getChartColor(key: ChartColorKey): string {
  return CHART_COLORS[key];
}
